import Config from '@/Config';
import HelpUrls from '@/Data/HelpUrls.json';
import { projectSearchSortType } from '@/Models';
import ProjectCardReprintStamp from '@/Components/ProductCard/ProjectCardReprintStamp.vue';

export const ecoSystem = {
    title: 'It\'s the BEST crowdfunding ecosystem for tabletop projects.',
    subtitle: 'Don’t just take our word for it - check out these 10 facts!',
    items: [{
        icon: 'handshake',
        text: '8 out of the top 10 tabletop creators choose Gamefound.',
        url: HelpUrls.BLOG_2024_SUMMARY,
    }, {
        icon: 'face-smile-beam',
        text: '87% of creators who switch to Gamefound stick with us.',
    }, {
        icon: 'bolt',
        text: 'Reprint campaigns raise 66% more on average on Gamefound.',
    }, {
        icon: 'ranking-star',
        text: '8 out of the 10 biggest board game campaigns in 2024 were on Gamefound.',
    }, {
        icon: 'chart-pie',
        text: 'Our Pledge Manager/Late Pledge tools bring in an average of 65% more funding.',
    }, {
        icon: 'money-bill-wave',
        text: '20+ payment methods – more than any other crowdfunding platform.',
        url: HelpUrls.HELP_PAYMENT_METHODS,
    }, {
        icon: 'rocket-launch',
        text: 'The fastest-growing platform in tabletop crowdfunding: from $46M in 2022 to $156M in 2024 – 72% average growth – and over 1.5 million dedicated users.',
    }, {
        icon: 'stretch-pay-icon',
        iconNameSpace: 'ga',
        text: 'Average 20% higher funding for campaigns using Stretch Pay.',
    },
    {
        icon: 'sparkles',
        text: 'A state-of-the-art Pledge Manager that’s helped manage over 2,400 campaigns in the past 8 years',
    },
    {
        icon: 'bullseye-arrow',
        text: 'Adfound – an elite marketing tool available to every creator, delivering an average ROAS (Return on Ad Spend) of 6.3x.',
    }],
};

export const quotes = {
    goOnBoard: {
        author: 'Paweł Podgrudny, Go On Board',
        text: 'Gamefound is a user-friendly and dependable choice for any board game creator. It combines great front-end design with efficient back-end systems, making it excellent for showcasing our projects and handling billing and orders.',
    },
    gabeBarrett: {
        author: 'Gabe Barrett',
        text: 'Gamefound isn\'t just the best platform to fund a game; it’s the best place to build a business. The creator tools, campaign builder, and customer service are as good as it gets.',
    },
    creativeGameStudio: {
        author: 'Creative Game Studio',
        text: 'From day one, we at CGS believed in Gamefound\'s vision and joined the platform during its beta phase, confident it was on the right path. Today, as the leading platform for board game crowdfunding, Gamefound has proven to be a reliable, innovative, and creator-focused partner. Their commitment to the industry and constant evolution make it clear they are not only here to stay - they are just getting started. We\'re proud to be part of that journey.',
    },
    chipTheoryGames: {
        author: 'Josh Wielgus, Creative Game Studio',
        text: 'Gamefound’s marketing efforts are affordable, effective, and fast! We delivered many assets from our games, and they made tremendous use of them in a huge variety of ads. Definitely highly recommended!',
    },
    hexPloreIt: {
        author: 'Jonathan Mariucci, HEXplore It',
        text: 'Huge commendations to Gamefound for the toolset they’ve designed and for their outstanding technical support. Gamefound is leagues ahead of its competitor and it shows.',
    },
};

export const features = [
    {
        name: '20+ Multiple payment methods',
        text: 'Let backers support your project using their preferred payment method – including regional options and digital wallets.',
        url: HelpUrls.HELP_PAYMENT_METHODS,
    },
    {
        name: 'Currency conversion',
        text: 'Make pledging cheaper for international backers with fixed, favorable currency conversion rates.',
        url: HelpUrls.HELP_CURRENCY_CONVERSION,
    },
    {
        name: 'Free flexible instalments',
        text: 'Give your backers the option to split payments. You decide the number of instalments and the minimum pledge amount.',
        url: HelpUrls.HELPADMIN_HOW_TO_SET_UP_STRETCH_PAY,
    },
    {
        name: 'Endgame',
        text: 'Turn the final days of your campaign into a fun, engaging event – and extend its life with community support.',
    },
    {
        name: 'Multi-language campaign support',
        text: 'Deliver a local experience by translating your entire project page into multiple languages.',
    },
    {
        name: 'Automated performance marketing',
        mark: 'adfound',
        text: 'Use Adfound – our powerful, automated marketing tool – to reach the right audience at the best possible rate.',
        url: Config.navigation.performanceMarketingUrl,
    },
    {
        name: 'Keeping followers after campaign',
        text: 'Stay connected. Your past campaign followers and backers get notified when you launch a new project.',
    },
    {
        name: 'Followers get all updates',
        text: 'Keep your audience engaged with updates and polls – even during the Upcoming phase.',
    },
    {
        name: 'Stable pledge',
        text: 'Build trust through transparency – offer a full refund if your post-campaign costs increase significantly.',
        url: HelpUrls.HELPADMIN_PROJECTSETTINGS_STABLE_PLEDGE,
    },
    {
        name: 'Advanced pledge manager',
        text: 'Handle shipping, inventory, and add-ons with powerful tools built natively into the platform.',
    },
];

export const buildProjectExplorerCategories = (projectCategories) => {
    const reprintsData = {
        ['guardsofatlantis2new']: 280,
        ['after-the-empire---deluxe-version-reprint']: 136,
        ['earthborne-rangers-reprint']: 450,
        ['assault-on-doomrock-ultimate-edition']: 116,
        ['gamefound-crowdfunding-campaign']: 500,
    };

    const categories = [
        {
            label: 'Major creators',
            key: 'majorCreators',
            urlName: 'major-creators',
        },
        {
            label: 'First-timers',
            key: 'firstTimers',
            urlName: 'first-timers',
        },
        {
            label: 'Reprints',
            key: 'reprints',
            urlName: 'reprints',
            sortType: projectSearchSortType[projectSearchSortType.newest].name,
            transformResults: (item) => {
                item.customLabels = [{
                    name: 'Reprint',
                    theme: 'default',
                }];

                if (reprintsData[item.projectUrlName]) {
                    item.customItemComponent = ProjectCardReprintStamp;
                    item.customItemComponentProps = {
                        value: reprintsData[item.projectUrlName],
                    };
                }

                return item;
            },
        },
        {
            label: 'Non-board games',
            key: 'nonBoardGame',
            urlName: 'non-board-game',

        },
    ];

    return categories.reduce((acc, category) => {
        const projectCategory = projectCategories.find(pc => pc.urlName === category.urlName);
        if (projectCategory) {
            category.searchParams = {
                projectCategories: [projectCategory.projectCategoryID],
                includeRefs: true,
                sortType: category.sortType || projectSearchSortType[projectSearchSortType.mostPopular].name,
            };
            acc.push(category);
        }
        return acc;
    }, []);
};